import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import { SearchModal } from 'components/atomic/atoms';
import { CartView } from 'components/atomic/molecules';
import { useUIContext } from 'components/context/uiContext/ManagedUiContext';
import { staticMediaStoreBaseURL } from 'config/common';
import { getPageURL, ROUTE_CONFIG } from 'services/hostPortal.service';

const onClickOfCartIcon = ({
  setPageState,
  cartList = [],
  setShowCartSelectorModal,
  router
}) => {
  const [{ id, isDefault } = {}] = cartList;
  const onlyDefaultCart = cartList.length === 1 && isDefault;
  if (onlyDefaultCart) {
    router.push(
      getPageURL({
        pageName: ROUTE_CONFIG.HOST_CART.label,
        pathParams: {
          cartId: id
        }
      })
    );
  } else {
    setPageState('cart');
    setShowCartSelectorModal(cartList.length > 0);
  }
};

const MobileNavbarOrganism = ({
  onPage,
  showSearchModal,
  setShowSearchModal
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const [pageState, setPageState] = useState(onPage);
  const [showCartSelectorModal, setShowCartSelectorModal] = useState(false);

  useEffect(() => {
    setShowSearchModal(false);
  }, [pathname]);

  const handleSearchClick = (e) => {
    e.preventDefault();
    setShowSearchModal(true);
  };

  const { cartItemCount, cartList, selectedCart } = useUIContext();

  return (
    <div className='fixed flex justify-between items-center bottom-0 z-151 h-16 w-full px-6 bg-white'>
      <Link
        href={getPageURL({
          pageName: ROUTE_CONFIG.HOME_PAGE.label
        })}
      >
        <div
          className='flex items-center flex-col cursor-pointer group'
          onClick={() => setPageState('home')}
        >
          <div
            className={`w-9 h-0.5 rounded-sm ${
              pageState === 'home' ? 'bg-brand-gradient' : 'bg-transparent'
            }`}
          ></div>
          <div className='py-3 flex items-center flex-col'>
            <div className='w-5.5 h-5.5 relative'>
              <Image
                src={
                  pageState === 'home'
                    ? `${staticMediaStoreBaseURL}/icons/home-color.svg`
                    : `${staticMediaStoreBaseURL}/icons/home-icon.svg`
                }
                alt='Home'
                height={22}
                width={22}
              />
            </div>

            <span
              className={`font-Inter text-10px font-normal hover:text-nero focus:text-nero group-hover:text-nero block leading-3 pt-1.5 ${
                pageState === 'home' ? 'text-nero' : 'text-silver'
              } `}
            >
              {' '}
              Home{' '}
            </span>
          </div>
        </div>
      </Link>

      <Link
        href={getPageURL({
          pageName: ROUTE_CONFIG.CATEGORIES_LIST.label
        })}
      >
        <div
          className='flex items-center flex-col cursor-pointer group'
          onClick={() => setPageState('category-listing')}
        >
          <div
            className={`w-9 h-0.5 rounded-sm ${
              pageState === 'category-listing'
                ? 'bg-brand-gradient'
                : 'bg-transparent'
            }`}
          ></div>

          <div className='py-3 flex items-center flex-col'>
            <div className='w-5.5 h-5.5 relative'>
              <Image
                src={
                  pageState === 'category-listing'
                    ? `${staticMediaStoreBaseURL}/icons/cat-color.svg`
                    : `${staticMediaStoreBaseURL}/icons/cat-icon.svg`
                }
                alt='Categories'
                height={22}
                width={22}
              />
            </div>

            <span
              className={`font-Inter text-10px font-normal hover:text-nero focus:text-nero group-hover:text-nero block leading-3 pt-1.5 ${
                pageState === 'category-listing' ? 'text-nero' : 'text-silver'
              } `}
            >
              {' '}
              Categories{' '}
            </span>
          </div>
        </div>
      </Link>

      <div
        className='flex items-center flex-col cursor-pointer group'
        onClick={handleSearchClick}
      >
        <div
          className={`w-9 h-0.5 rounded-sm ${
            showSearchModal ? 'bg-brand-gradient' : 'bg-transparent'
          }`}
        ></div>

        <div className='py-3 flex items-center flex-col'>
          <div className='w-5.5 h-5.5 relative'>
            <Image
              src={
                showSearchModal
                  ? `${staticMediaStoreBaseURL}/icons/search-pink.svg`
                  : `${staticMediaStoreBaseURL}/icons/search-gray.svg`
              }
              alt='Search'
              height={22}
              width={22}
            />
          </div>
          <span
            className={`font-Inter text-10px font-normal hover:text-nero focus:text-nero group-hover:text-nero block leading-3 pt-1.5 ${
              pageState === 'search' ? 'text-nero' : 'text-silver'
            } `}
          >
            {' '}
            Explore{' '}
          </span>
        </div>
      </div>

      <div className='flex items-center flex-col cursor-pointer group'>
        <div
          className={`w-9 h-0.5 rounded-sm ${
            pageState === 'cart' ? 'bg-brand-gradient' : 'bg-transparent'
          }`}
        ></div>

        <div className='py-3 flex items-center flex-col relative'>
          <div className='w-5.5 h-5.5 relative'>
            {pageState === 'cart' ? (
              <CartView
                {...{
                  cartItemCount,
                  cartList,
                  router,
                  selectedCart,
                  setShowCartSelectorModal,
                  showCartSelectorModal
                }}
              />
            ) : (
              <Image
                src={`${staticMediaStoreBaseURL}/icons/cart-gray.svg`}
                alt='Cart'
                sizes='100vw'
                style={{ objectFit: 'cover', height: '22px', width: '22px' }}
                height={22}
                width={22}
                onClick={() =>
                  onClickOfCartIcon({
                    setPageState,
                    setShowCartSelectorModal,
                    cartList,
                    router
                  })
                }
              />
            )}

            {cartItemCount > 0 && pageState !== 'cart' && (
              <span
                className={`absolute top-0 -right-3 flex items-center justify-center rounded-full h-4 w-4 text-white text-xxs leading-2 font-Inter font-normal ${
                  pageState === 'cart' ? 'bg-cart' : 'bg-dim-gray'
                }`}
              >
                {' '}
                {cartItemCount}{' '}
              </span>
            )}
          </div>

          <span
            className={`font-Inter text-10px font-normal hover:text-nero focus:text-nero group-hover:text-nero block leading-3 pt-1.5 ${
              pageState === 'cart' ? 'text-nero md:pl-2' : 'text-silver'
            } `}
          >
            {' '}
            Cart{' '}
          </span>
        </div>
      </div>

      <Link
        href={getPageURL({
          pageName: ROUTE_CONFIG.USER_PROFILE.label
        })}
      >
        <div
          className='flex items-center flex-col cursor-pointer group'
          onClick={() => setPageState('user-profile')}
        >
          <div
            className={`w-9 h-0.5 rounded-sm ${
              pageState === 'user-profile'
                ? 'bg-brand-gradient'
                : 'bg-transparent'
            }`}
          ></div>

          <div className='py-3 flex items-center flex-col'>
            <div className='w-5.5 h-5.5 relative'>
              <Image
                src={
                  pageState === 'user-profile'
                    ? `${staticMediaStoreBaseURL}/icons/person-round-color.svg`
                    : `${staticMediaStoreBaseURL}/icons/person-round.svg`
                }
                alt='User Profile'
                sizes='100vw'
                style={{ objectFit: 'cover', width: '22px', height: '22px' }}
                height={22}
                width={22}
              />
            </div>

            <span
              className={`font-Inter text-10px font-normal hover:text-nero focus:text-nero group-hover:text-nero block leading-3 pt-1.5 ${
                pageState === 'user-profile' ? 'text-nero' : 'text-silver'
              } `}
            >
              {' '}
              Profile{' '}
            </span>
          </div>
        </div>
      </Link>

      {showSearchModal && (
        <SearchModal setShowSearchModal={setShowSearchModal} />
      )}
    </div>
  );
};

export default MobileNavbarOrganism;
